import { supabase } from './supabase';

export interface ArticleFeedback {
  article_slug: string;
  rating: number;
  comment?: string;
  email?: string;
}

export interface FeedbackStats {
  avgRating: number;
  totalFeedback: number;
  ratings: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

// Submit feedback for an article
export async function submitArticleFeedback(feedback: ArticleFeedback) {
  try {
    const { data, error } = await supabase
      .from('article_feedback')
      .insert([feedback]);
      
    if (error) {
      throw error;
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return { success: false, error };
  }
}

// Get feedback statistics for an article
export async function getArticleFeedbackStats(articleSlug: string): Promise<FeedbackStats | null> {
  try {
    // Get all ratings for this article
    const { data, error } = await supabase
      .from('article_feedback')
      .select('rating')
      .eq('article_slug', articleSlug);
      
    if (error) {
      throw error;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    // Calculate average rating
    const ratings = data.map(item => item.rating);
    const avgRating = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    
    // Count each rating
    const ratingCounts = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    };
    
    ratings.forEach(rating => {
      if (rating >= 1 && rating <= 5) {
        ratingCounts[rating as 1|2|3|4|5]++;
      }
    });
    
    return {
      avgRating,
      totalFeedback: ratings.length,
      ratings: ratingCounts
    };
  } catch (error) {
    console.error('Error getting feedback stats:', error);
    return null;
  }
}

// Check if user has already submitted feedback for this article
// Uses a combination of IP address and article slug to identify unique users
export async function hasUserSubmittedFeedback(articleSlug: string): Promise<boolean> {
  try {
    // Note: This relies on the Edge Function in Supabase capturing the IP address
    const { count, error } = await supabase
      .from('article_feedback')
      .select('id', { count: 'exact', head: true })
      .eq('article_slug', articleSlug);
      
    if (error) {
      console.error('Error checking for existing feedback:', error);
      return false;
    }
    
    return count !== null && count > 0;
  } catch (error) {
    console.error('Error checking for existing feedback:', error);
    return false;
  }
} 